import React, { useEffect } from 'react';
import { trackEvent, trackPurchaseClick } from "../../utils/Analytics";
import LegalLinks from "../../components/LegalLinks";
import FounderImage from "../../images/founder.png";
import DeckImage from "../../images/deck-mockup.png";
import LettuceLogo from "../../images/logo.png";
import LettuceThinkingImage from "../../images/lettuce-thinking.png";
import AppMCAADLightImage from "../../images/app-mcaad-light.png";
import CardsPlayImage from "../../images/cards-play-2.png";
import { ActionButton } from "../../components/Buttons";
import DigitalWallBackground from "../../components/DigitalWallBackground";
import { MCAAD_WALL_REFINE_PATH } from "../../utils/empathyAI/refineContexts";
import { DEFAULT_WALL_PATH } from "../../utils/cards/cardConstants";

const scrollToSection = (event, sectionId) => {
  const section = document.getElementById(sectionId);

  if (!section) return;

  event.preventDefault();
  section.scrollIntoView({ behavior: "smooth", block: "start" });
};

const ScrollMoreIcon = () => (
  <a
    href="#mcaad-after-hero"
    onClick={(event) => scrollToSection(event, "mcaad-after-hero")}
    className="mcaad-scroll-more absolute bottom-6 left-1/2 z-40 flex h-16 w-16 -translate-x-1/2 flex-col items-center justify-center gap-1 rounded-full border border-white/60 bg-white/58 font-sans text-[9px] font-black uppercase tracking-[0.16em] text-[#2f2a16]/72 shadow-[0_12px_34px_rgba(78,67,24,0.14)] backdrop-blur-md transition-colors hover:text-[#245c07] sm:h-auto sm:w-auto sm:gap-2 sm:rounded-none sm:border-0 sm:bg-transparent sm:text-[10px] sm:tracking-[0.2em] sm:shadow-none sm:backdrop-blur-0"
    aria-label="Scroll for more"
  >
    <span>Scroll</span>
    <span className="h-4 w-4 rotate-45 border-b-2 border-r-2 border-current" aria-hidden="true" />
  </a>
);

const HOW_IT_WORKS_STEPS = [
  {
    step: "1",
    title: "Start with what feels hard to ask",
    body: "Start with the messy thought, question, or worry you wish you could ask a loved one.",
    image: LettuceThinkingImage,
    imageAlt: "LettuceTalk character thinking about a question",
    imageClassName: "object-contain p-4",
  },
  {
    step: "2",
    title: "Choose the card that feels best",
    body: "Use Empathy AI to soften the wording, shape the tone, and translate it into your loved one's language if needed.",
    image: AppMCAADLightImage,
    imageAlt: "LettuceTalk digital card creation preview",
    imageClassName: "object-cover object-top",
  },
  {
    step: "3",
    title: "Share it privately or add it to the wall",
    body: "Keep your card private, send it to someone you love, or contribute it to the Care Wall.",
    image: CardsPlayImage,
    imageAlt: "LettuceTalk cards laid out together",
    imageClassName: "object-cover object-center",
  },
];

const MCAADView = () => {
  const colors = {
    lettuceGreen: '#307609',
    bgCream: '#fffdf1',
  };

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden font-serif" style={{ backgroundColor: colors.bgCream }}>
      <style>
        {`
          @keyframes scroll-more-drift {
            0%, 100% { transform: translate3d(-50%, 0, 0); opacity: 0.58; }
            50% { transform: translate3d(-50%, 8px, 0); opacity: 1; }
          }

          @media (prefers-reduced-motion: reduce) {
            .mcaad-scroll-more {
              animation: none !important;
            }
          }

          .mcaad-scroll-more {
            animation: scroll-more-drift 2.8s ease-in-out infinite;
          }
        `}
      </style>

      <main className="w-full overflow-x-hidden pb-16 sm:pb-24">
        <section className="relative min-h-[100svh] w-full text-center">
          <div className="relative min-h-[100svh] overflow-hidden">
            <DigitalWallBackground />

            <div className="absolute left-1/2 top-[49%] z-40 w-[min(92vw,576px)] -translate-x-1/2 -translate-y-1/2 sm:top-[48%] sm:w-[min(90vw,576px)]">
              <div className="relative mx-auto rounded-[1.75rem] border border-[#f4d94e]/58 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.96)_0%,rgba(255,255,255,0.72)_28%,rgba(255,248,190,0.62)_58%,rgba(255,255,255,0.54)_100%)] px-5 pb-6 pt-12 shadow-[0_0_38px_rgba(244,217,78,0.42),0_28px_90px_rgba(78,67,24,0.2),inset_0_0_34px_rgba(255,255,255,0.72)] backdrop-blur-xl sm:rounded-[2.25rem] sm:px-10 sm:pb-10 sm:pt-16">
                <div className="pointer-events-none absolute -inset-px rounded-[2rem] bg-[linear-gradient(135deg,rgba(255,255,255,0.72),transparent_38%,rgba(244,217,78,0.26)_72%,rgba(255,255,255,0.62))] opacity-80 sm:rounded-[2.25rem]" />
                <div className="absolute left-1/2 top-0 z-10 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/80 bg-[radial-gradient(circle_at_34%_24%,rgba(255,255,255,0.98)_0%,rgba(255,255,255,0.78)_34%,rgba(255,253,241,0.42)_66%,rgba(255,244,140,0.2)_100%)] shadow-[0_0_54px_rgba(255,255,255,0.95),0_0_42px_rgba(244,217,78,0.55),0_18px_42px_rgba(78,67,24,0.18),inset_0_0_18px_rgba(255,255,255,0.92)] backdrop-blur-md sm:h-28 sm:w-28">
                  <div className="pointer-events-none absolute left-4 top-3 h-7 w-7 rounded-full bg-white/74 blur-[2px] sm:left-6 sm:top-5 sm:h-9 sm:w-9" />
                  <img
                    src={LettuceLogo}
                    alt="LettuceTalk"
                    className="relative h-16 w-16 object-contain p-1 drop-shadow-[0_8px_14px_rgba(48,118,9,0.2)] sm:h-[86px] sm:w-[86px]"
                  />
                </div>

                <div className="relative z-[1] space-y-1 sm:space-y-2">
                  <div className="flex items-center justify-center gap-2 sm:gap-3">
                    <span className="font-title text-2xl font-bold leading-none tracking-[-0.04em] text-[#245f07] sm:text-3xl">
                      LettuceTalk
                    </span>
                  </div>

                  <p className="font-sans text-xs font-gray-400 text-gray-700 sm:text-sm">
                    by Impact Moonshots
                  </p>
                </div>

                <h1 className="relative z-[1] mx-auto mt-4 max-w-[500px] font-serif text-[2rem] font-medium leading-[0.9] tracking-[-0.04em] text-gray-950 sm:mt-5 sm:text-5xl sm:tracking-[-0.035em]">
                  Create a gentle card for someone you care about.
                </h1>

                <p className="relative z-[1] mx-auto mt-3 max-w-md font-sans text-sm leading-relaxed text-gray-700 sm:mt-4 sm:text-base">
                  Start with a question, worry, or thought that feels hard to say. LettuceTalk turns it into a gentle card you can share with someone you care about.
                </p>

                <div className="relative z-[1] mx-auto mt-5 flex max-w-xs flex-col items-center gap-2.5 sm:mt-6 sm:gap-3">
                  <ActionButton
                    href={MCAAD_WALL_REFINE_PATH}
                    onClick={() => trackEvent("CTA", "Start MCAAD Refine Flow", "MCAAD")}
                    fullWidth
                    icon="plus"
                    size="lg"
                  >
                    Create Your Card
                  </ActionButton>

                  <ActionButton
                    href={DEFAULT_WALL_PATH}
                    onClick={() => trackEvent("CTA", "View Care Wall MCAAD Hero", "MCAAD")}
                    fullWidth
                    icon="users"
                    variant="secondaryWarm"
                  >
                    View Care Wall
                  </ActionButton>
                </div>
              </div>
            </div>
            <ScrollMoreIcon />
          </div>
        </section>

        <div id="mcaad-after-hero" className="mx-auto w-[min(92vw,576px)] space-y-12 pt-12 sm:w-[min(90vw,576px)] sm:space-y-20 sm:pt-16">
          <section className="rounded-[1.75rem] border border-[#e8d983]/58 bg-white/72 p-4 shadow-[0_22px_70px_rgba(78,67,24,0.12),inset_0_1px_0_rgba(255,255,255,0.8)] backdrop-blur-xl sm:rounded-[2rem] sm:p-7">
            <div className="mx-auto mb-7 max-w-md px-2 pt-1 text-center sm:mb-8 sm:px-4 sm:pt-2">
              <p className="font-sans text-[10px] font-black uppercase tracking-[0.24em] text-[#307609]">
                How it works
              </p>
              <h2 className="mx-auto mt-3 max-w-sm font-serif text-[2.15rem] font-medium leading-[0.9] tracking-[-0.04em] text-gray-950 sm:max-w-md sm:text-5xl sm:tracking-[-0.035em]">
                From hard thought to gentle card.
              </h2>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 sm:gap-4">
              {HOW_IT_WORKS_STEPS.map(({ step, title, body, image, imageAlt, imageClassName }) => (
                <div key={step} className="rounded-2xl border border-[#307609]/10 bg-[#fffdf1]/86 p-3.5 text-left shadow-[0_10px_28px_rgba(78,67,24,0.07)] sm:p-4">
                  <div className="mb-3 flex items-center gap-3 sm:mb-4 sm:block">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#307609] font-sans text-sm font-black text-white shadow-[0_10px_20px_rgba(48,118,9,0.22)] sm:h-9 sm:w-9">
                      {step}
                    </div>
                    <h2 className="font-sans text-sm font-black text-gray-950 sm:mt-4">
                      {title}
                    </h2>
                  </div>

                  <p className="mt-2 font-sans text-sm leading-relaxed text-gray-600">
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section id="bring-the-deck-home" className="space-y-4 scroll-mt-8">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm flex flex-col">
              <div className="h-72 w-full overflow-hidden bg-gray-100 sm:h-96">
                <img
                  src={DeckImage}
                  alt="LettuceTalk Physical Deck"
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="space-y-7 p-6 sm:space-y-8 sm:p-8">
                <div className="space-y-3 text-left sm:text-center">
                  <h3 className="font-serif text-3xl font-medium leading-[0.92] tracking-[-0.035em] text-gray-950 sm:text-5xl">Bring the conversation home.</h3>
                  <p className="max-w-md font-sans text-[15px] leading-relaxed text-gray-600 sm:mx-auto">
                    Continue the experience with a physical LettuceTalk deck. Available at the <span className="font-bold text-gray-900">MCAAD Gift Shop (1st Floor)</span> while supplies last.           Not at the museum, or want to send a deck as a gift? You can also order one for home delivery at the link below.
                  </p>
                </div>

                <div className="space-y-3">
                  <ActionButton
                    href="https://buy.stripe.com/8x2bJ17g71w40Ut4aifQI05"
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    icon="order"
                    size="lg"
                    onClick={() =>
                      trackPurchaseClick({
                        surface: "mcaad_landing",
                        placement: "home_delivery",
                        partnerId: "mcaad",
                      })
                    }
                  >
                    Order for Home Delivery
                  </ActionButton>
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-6 rounded-2xl border border-gray-200/50 bg-[#f9f8f0] p-6 sm:space-y-8 sm:p-10">
            <div className="flex items-center gap-4">
              <img src={FounderImage} alt="Michelle" className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm" />
              <div className="text-left">
                <p className="font-sans font-bold text-gray-900 leading-none">Michelle Park</p>
                <p className="font-sans text-[10px] text-gray-500 uppercase tracking-widest pt-1">Creator of LettuceTalk & Milken Scholar</p>
              </div>
            </div>
            <blockquote className="font-serif text-xl text-gray-800 leading-relaxed italic border-l-4 border-[#307609]/20 pl-6">
              "I originally created LettuceTalk for my mom as we prepared for her care. I'm honored to bring this piece of my story to MCAAD."
            </blockquote>
            <p className="font-sans text-base leading-relaxed text-gray-700">Stop by MCAAD's Tapestry of Dreams to see Michelle's story.</p>
          </section>
        </div>
      </main>

      <footer className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-xl mx-auto px-6 flex flex-col items-center space-y-4">
          <div className="flex items-center gap-6 md:gap-8">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[12px] font-sans font-bold uppercase tracking-widest text-gray-500 hover:text-[#307609] transition-colors"
            >
              LettuceTalk
            </a>
            <span className="text-gray-200">|</span>
            <a
              href="https://mcaad.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[12px] font-sans font-bold uppercase tracking-widest text-gray-500 hover:text-black transition-colors"
            >
              MCAAD
            </a>
          </div>

          <p className="text-[10px] font-sans text-gray-400 uppercase tracking-widest">
            Impact Moonshots © 2026
          </p>
          <div className="text-[11px] font-sans text-gray-400">
            <LegalLinks
              linkClassName="hover:text-gray-700"
              analyticsClassName="hover:text-gray-700"
            />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MCAADView;
